import { observer } from "mobx-react-lite";
import { usePostHog } from "posthog-js/react";
import React from "react";
import type AircraftModel from "../model/AircraftModel";
import {
	changeExitFlightLevelOfAircraft,
	changeFlightLevelOfAircraft,
	handlePublishPromise,
	persistACCFlightLevel,
	persistAssignedFlightLevel,
	// persistLocalAssignedFlightLevel,
	persistNextSectorFlightLevel,
} from "../mqtt-client/publishers";
import { PilotRequestType } from "../schemas/pilotRequestSchema";
import { configurationStore, cwpStore, roleConfigurationStore } from "../state";
import { clearMatchingTaRequests } from "../utils/teamAssistantHelper";
import {
	type CommunicationMethod,
	PopupCommunicationButtons,
} from "./shared/CommunicationButtons";

function ListOfLevels(properties: {
	value: number;
	onClick: (index: number) => void;
	topFlightLevel: number | undefined;
	bottomFlightLevel: number | undefined;
}) {
	const { value, onClick, topFlightLevel, bottomFlightLevel } = properties;
	const levels = Array.from(
		{ length: Math.floor((560 - 210) / 10) + 1 },
		(_, listIndex) => 560 - listIndex * 10,
	);

	const hasLevel =
		topFlightLevel !== undefined && bottomFlightLevel !== undefined;

	return (
		<>
			{levels.map((index) => {
				const isWithinRange =
					hasLevel && index >= bottomFlightLevel && index <= topFlightLevel;
				const isSelected = index === value;

				return (
					<button
						key={index}
						onClick={(): void => onClick(index)}
						className={`
						    block px-0 py-1 text-xs text-center
						    bg-[#1e3a5f] text-white
						    hover:bg-[#4b90db]
						    border-none outline-none
						    ${isWithinRange ? "font-bold" : ""}
						`}
						data-level={index}
					>
						{isSelected ? (
							<>
								<span>&gt;</span>&nbsp;{index}&nbsp;<span>&lt;</span>
							</>
						) : (
							index
						)}
					</button>
				);
			})}
		</>
	);
}

export default observer(function AircraftLevelPopup(properties: {
	aircraft: AircraftModel;
}) {
	const posthog = usePostHog();
	const {
		aircraftId,
		assignedFlightId,
		lastKnownAltitude: altitude,
		callSign,
		controlledBy,
		nextACCFL,
		nextSectorFL,
		assignedFlightLevel,
		// setLocalAssignedFlightLevel,
	} = properties.aircraft;

	const { areaOfIncludedAirspaces, currentCWP } = configurationStore;
	const { currentControlledSector } = roleConfigurationStore;

	const currentAircraftLevel = Math.round(altitude / 10) * 10;

	const parseLevel = (value: string, multiplier: 1 | 10 = 1): number | null => {
		if (!/^\d+$/.test(value)) {
			return null;
		}

		return Math.round((Number.parseInt(value, 10) * multiplier) / 10) * 10;
	};

	const activeMode: "next_sector_fl" | "next_acc_fl" | "assigned_fl" =
		cwpStore.nextSectorFlActivated
			? "next_sector_fl"
			: cwpStore.flightLevelNextAccActivated
				? "next_acc_fl"
				: "assigned_fl";

	// Set selected level from the value being edited in the current popup mode.
	const getDefaultLevel = (): number => {
		if (activeMode === "next_sector_fl") {
			const parsedNextSectorFL = parseLevel(nextSectorFL, 10);
			if (parsedNextSectorFL !== null) {
				return parsedNextSectorFL;
			}
		}

		if (activeMode === "next_acc_fl") {
			const parsedNextACCFL = parseLevel(nextACCFL);
			if (parsedNextACCFL !== null) {
				return parsedNextACCFL;
			}
		}

		if (activeMode === "assigned_fl") {
			const parsedAssignedFL = parseLevel(assignedFlightLevel);
			if (parsedAssignedFL !== null) {
				return parsedAssignedFL;
			}
		}

		return currentAircraftLevel;
	};
	const defaultLevel = getDefaultLevel();

	const [flightLevel, setFlightLevel] = React.useState(defaultLevel);
	const listOfLevelsReference = React.createRef<HTMLDivElement>();
	const shouldShow = cwpStore.aircraftsWithLevelPopup.has(aircraftId);

	const airspaceCurrent = areaOfIncludedAirspaces.find(
		({ sectorId }) => sectorId === currentControlledSector,
	);

	const topFlightLevel = airspaceCurrent?.topFlightLevel;
	const bottomFlightLevel = airspaceCurrent?.bottomFlightLevel;

	// Reset flight level to default when popup opens
	React.useEffect(() => {
		if (shouldShow) {
			setFlightLevel(defaultLevel);
			posthog?.capture("altitude_popup_opened", {
				aircraft_id: aircraftId,
				callsign: callSign,
				current_altitude: currentAircraftLevel,
				default_level: defaultLevel,
				active_mode: activeMode,
			});
		}
	}, [
		shouldShow,
		defaultLevel,
		posthog,
		aircraftId,
		callSign,
		currentAircraftLevel,
		activeMode,
	]);

	React.useEffect(() => {
		// Scroll to the level in the list (only within the container, not the whole page)
		const container = listOfLevelsReference.current;
		if (!container) {
			return;
		}

		const listElement = ([...container.children] as HTMLButtonElement[]).find(
			(element) => element.dataset.level === `${flightLevel}`,
		);

		if (listElement) {
			// Calculate scroll position to center the element within the container
			// Using getBoundingClientRect for accurate positioning
			const containerRect = container.getBoundingClientRect();
			const elementRect = listElement.getBoundingClientRect();
			const elementTopRelativeToContainer =
				elementRect.top - containerRect.top + container.scrollTop;
			container.scrollTop =
				elementTopRelativeToContainer -
				container.clientHeight / 2 +
				listElement.offsetHeight / 2;
		}
	}, [flightLevel, shouldShow, listOfLevelsReference]);

	const accepted = controlledBy === currentCWP;
	const isMaster = currentCWP === "All";
	if (!shouldShow) {
		return null;
	}

	const sliderStep = 10;
	const flightLevelMin = 210;
	const flightLevelMax = 560;

	const FlightLevelChange = (direction: "up" | "down"): void => {
		const newStepValue = Math.min(
			flightLevelMax,
			Math.max(
				flightLevelMin,
				direction === "up"
					? flightLevel + sliderStep
					: flightLevel - sliderStep,
			),
		);
		setFlightLevel(newStepValue);
	};

	const close = (): void => {
		cwpStore.clearTaRequestCallback();
		cwpStore.closeLevelPopupForAircraft(aircraftId);
		posthog?.capture("altitude_popup_closed", {
			aircraft_id: aircraftId,
			callsign: callSign,
			action: "cancelled",
		});
	};

	const applyFlightLevel = (
		level: number,
		method: CommunicationMethod,
	): void => {
		const communicationMethod: CommunicationMethod =
			method === "dl" ? "dl" : "rt";
		const stringFlightLevel = level.toString();

		// If TA request callback is set, call it instead of normal behavior
		if (cwpStore.taRequestCallback) {
			cwpStore.taRequestCallback(stringFlightLevel);
			cwpStore.clearTaRequestCallback();
			cwpStore.closeLevelPopupForAircraft(aircraftId);
			return;
		}

		const previousAltitude = currentAircraftLevel;
		let changeType: "next_sector_fl" | "next_acc_fl" | "assigned_fl";

		if (cwpStore.nextSectorFlActivated) {
			changeType = "next_sector_fl";
			cwpStore.showNSFL(false);
			properties.aircraft.setNextSectorFL(stringFlightLevel);
			handlePublishPromise(
				persistNextSectorFlightLevel(assignedFlightId, stringFlightLevel),
			);
			handlePublishPromise(
				changeExitFlightLevelOfAircraft(
					controlledBy,
					assignedFlightId,
					stringFlightLevel,
				),
			);
		} else if (cwpStore.flightLevelNextAccActivated) {
			changeType = "next_acc_fl";
			cwpStore.showFlACC(false);
			properties.aircraft.setNextACCFL(stringFlightLevel, true); // Mark as manually set
			handlePublishPromise(
				persistACCFlightLevel(assignedFlightId, stringFlightLevel),
			);
			// Changing the level when changing the FL - COO - with delay - how much?
			setTimeout(() => {
				handlePublishPromise(
					changeFlightLevelOfAircraft(
						controlledBy,
						assignedFlightId,
						stringFlightLevel,
					),
				);
			}, 1000);
		}
		// We don't have local assigned right? --> This is why FL is not set.
		// else if (!cwpStore.pseudoPilot && !isMaster) {
		// 	changeType = "local_assigned_fl";
		// 	setLocalAssignedFlightLevel(stringFlightLevel);
		// 	handlePublishPromise(
		// 		persistLocalAssignedFlightLevel(assignedFlightId, stringFlightLevel),
		// 	);
		// }
		else {
			changeType = "assigned_fl";
			properties.aircraft.setAssignedFlightLevel(stringFlightLevel);
			handlePublishPromise(
				changeFlightLevelOfAircraft(
					controlledBy,
					assignedFlightId,
					stringFlightLevel,
				),
			);
			handlePublishPromise(
				persistAssignedFlightLevel(assignedFlightId, stringFlightLevel),
			);
		}

		posthog?.capture("altitude_changed", {
			aircraft_id: aircraftId,
			callsign: callSign,
			previous_altitude: previousAltitude,
			new_altitude: level,
			altitude_change: level - previousAltitude,
			change_type: changeType,
			controlled_by: controlledBy,
			current_cwp: currentCWP,
			is_master: isMaster,
			communication_method: communicationMethod,
		});

		clearMatchingTaRequests(properties.aircraft, [
			PilotRequestType.FlightLevel,
		]);
		cwpStore.closeLevelPopupForAircraft(aircraftId);
	};

	const setFLCP = (method: CommunicationMethod): void => {
		applyFlightLevel(flightLevel, method);
	};

	// Handler for clicking a flight level button in the list
	const handleFlightLevelClick = (level: number): void => {
		// In TA request mode, always apply (even if clicking default level)
		if (cwpStore.taRequestCallback) {
			applyFlightLevel(level, "rt");
			return;
		}
		if (level === defaultLevel) {
			// If clicking on the default level (no change), just close the popup
			close();
		} else {
			// Otherwise, apply immediately
			applyFlightLevel(level, "rt");
		}
	};

	const isTaRequestMode = !!cwpStore.taRequestCallback;

	return (
		<div
			className={`
			w-20 bg-[#1e3a5f] p-0 shadow-lg
			${accepted ? "border-2 border-green-400" : ""}
			${isTaRequestMode ? "border-2 border-yellow-400" : ""}
		`}
			style={{ borderRadius: 0 }}
		>
			{isTaRequestMode && (
				<div className="text-center text-[10px] py-0.5 bg-yellow-500 text-black font-medium">
					TA Request
				</div>
			)}
			<div className="text-center font-bold text-xs py-1 bg-black text-white">
				{callSign}
			</div>
			<div className="flex flex-col">
				<button
					onClick={(): void => FlightLevelChange("up")}
					className="btn btn-ghost btn-xs text-xs"
				>
					▲
				</button>
				<div
					className="h-40 overflow-y-scroll scrollbar-hide bg-[#1e3a5f] flex flex-col"
					ref={listOfLevelsReference}
				>
					<ListOfLevels
						value={flightLevel}
						onClick={handleFlightLevelClick}
						topFlightLevel={topFlightLevel}
						bottomFlightLevel={bottomFlightLevel}
					/>
				</div>
				<button
					onClick={(): void => FlightLevelChange("down")}
					className="btn btn-ghost btn-xs text-xs"
				>
					▼
				</button>
			</div>
			<PopupCommunicationButtons
				hasCPDLC={properties.aircraft.hasCPDLC}
				onSubmit={setFLCP}
				onClose={close}
			/>
		</div>
	);
});
